<template>
  <div class="edit-sheet-field">
    <raw-controls v-ps :rawControls="rawControls" class="side-menu"></raw-controls>
    <section class="main-content">
      <fields-content
        v-ps
        v-loading="loading"
        ref="fieldsContent"
        :sheet="sheet"
        :mainField="mainField"
        :btnArray="btnArray"
        :activeControl="control"
        :overviewFields="overviewFields"
        :progress="progress"
        :allFields="allFields"
        @openSetting="openSetting"
        @update="update"
        class="fields-box"
      ></fields-content>
      <detail-setting-content
        ref="settingContent"
        class="setting-box"
        :type="settingType"
        :sheet="sheet"
        :control="control"
        :controlsMap="controlsMap"
        :btnArray="btnArray"
        :fields="allFields"
        :overviewFields="overviewFields"
        :progress="progress"
        :availableSheets="availableSheets"
        @update="update"
      ></detail-setting-content>
    </section>
  </div>
</template>

<script>
import RawControls from "./raw-controls"
import FieldsContent from "./fields-content"
import DetailSettingContent from "./detail-setting-content"
import { basicBtns, controlRequiredAttrsMap } from "~/data/sheet"
import { deepClone, isEmpty, randomString } from "@/utils/common"
import { circleDetect } from "@/utils/algorithm"
import { transControlValRelyMap } from "@/utils/sheet"

export default {
  name: "edit-sheet-field",
  components: {
    RawControls,
    FieldsContent,
    DetailSettingContent
  },
  props: {
    sheet: Object,
    rawControls: Object,
    controlsMap: Object,
    customBtns: Object,
    availableSheets: Object
  },
  data() {
    return {
      basicBtns,
      controlRequiredAttrsMap,
      notEditedSheet: {},
      fieldsGroup: [],
      overviewFields: [],
      progress: {
        stepCid: "",
        stepList: []
      },
      hiddenCids: [],
      btnArray: [],
      loading: true,
      settingType: "",
      control: null
    }
  },
  computed: {
    mainField() {
      if (this.sheet.loading) return {}
      return this.allFields.find(i => i.main) || {}
    },
    allFields() {
      return this.fieldsGroup.reduce((acc, cur) => ((acc = acc.concat(cur.fields)), acc), [])
    },
    controlValRelyMap() {
      return transControlValRelyMap(this.allFields)
    }
  },
  deactivated() {
    this.loading = false
    this.settingType = ""
    this.control = null
  },
  methods: {
    // 初始化
    init(sheetInfo) {
      this.openSetting({ type: "reset" })
      this.initBtnArray()
      this.initFieldsGroup()
      this.initOverviewFields()
      this.initProgress()
      this.saveSheet()
      this.loading = false
    },
    // 初始化所有按钮
    initBtnArray() {
      let customBtns = this.customBtns.data.map(i => ({
        label: i.name,
        title: i.name,
        value: i.id,
        desc: i.desc,
        icon: i.icon ? "button_" + i.icon : "button_icon-01",
        bgColor: i.color && i.color !== "#f5f7fa" ? i.color : "#fff",
        textColor: i.color && i.color !== "#f5f7fa" ? "#fff" : "#1989fa",
        iconColor: i.color && i.color !== "#f5f7fa" ? i.color : "#313132"
      }))
      let allBtns = this.basicBtns
        .map(btn => {
          btn.iconColor = "#313132"
          btn.title = btn.label
          btn.bgColor = "#fff"
          btn.textColor = "#1989fa"
          return btn
        })
        .concat(customBtns)
      let allBtnMap = allBtns.reduce((acc, cur) => ((acc[cur.value] = cur), acc), {})
      let btns = [],
        btnLayout = this.sheet.data.layout.btnList
      if (!btnLayout || btnLayout.length == 0) btns = allBtns
      else {
        for (let index = 0; index < btnLayout.length; index++) {
          if (allBtnMap[btnLayout[index]]) btns.push(allBtnMap[btnLayout[index]])
        }
        let exist = btns.map(i => i.value)
        for (let item of allBtns) {
          if (!exist.includes(item.value)) btns.push(item)
        }
      }
      this.btnArray = btns
    },
    // 初始化字段分组
    initFieldsGroup() {
      let fieldsGroup = []
      let cnontrolsMap = this.sheet.data.controls.reduce((acc, cur) => {
        acc[cur._cid] = cur
        return acc
      }, {})
      let groupList = this.sheet.data.layout.groupList
      if (!groupList || groupList.length == 0)
        fieldsGroup = [{ name: "基本信息", id: randomString(10), fields: this.sheet.data.controls }]
      else {
        fieldsGroup = groupList.map(group => {
          return {
            id: randomString(10),
            name: group.name,
            // reduce()累加器，acc代表原数组，cid代表处理数组元素
            fields: group.cidList.reduce((acc, cid) => {
              if (cnontrolsMap[cid]) acc.push(cnontrolsMap[cid])
              return acc
            }, [])
          }
        })
      }
      this.fieldsGroup = fieldsGroup
      this.hiddenCids = this.sheet.data.layout.hiddenCids
      this.$refs.fieldsContent.init(fieldsGroup, this.hiddenCids)
    },
    // 初始化摘要字段
    initOverviewFields() {
      let fields = []
      let cnontrolsMap = this.sheet.data.controls.reduce((acc, cur) => {
        acc[cur._cid] = cur
        return acc
      }, {})
      let summaryFields = this.sheet.data.layout.summary
      fields = summaryFields.reduce((acc, cur) => {
        if (cnontrolsMap[cur]) acc.push(cnontrolsMap[cur])
        return acc
      }, [])
      this.overviewFields = fields
    },
    // 初始化步骤条
    initProgress() {
      this.progress.stepCid = this.sheet.data.layout.stepCid || ""
      this.progress.stepList = this.sheet.data.layout.stepList || []
    },
    // 初始化布局
    getLayoutInfo() {
      return {
        sheetId: this.sheet.data.sheetId,
        groupList: this.fieldsGroup.map(group => {
          return {
            name: group.name,
            cidList: group.fields.map(i => i._cid || i.code)
          }
        }),
        hiddenCids: this.hiddenCids,
        summary: this.overviewFields.map(i => i._cid),
        stepCid: this.progress.stepCid,
        stepList: this.progress.stepList,
        btnList: this.btnArray.map(i => i.value),
        extraSetting: this.sheet.data.layout.extraSetting
      }
    },
    // 保存未被编辑过的 sheet
    saveSheet() {
      this.sheet.data.layout = this.getLayoutInfo()
      this.notEditedSheet = deepClone(this.sheet.data)
    },
    // 打开右侧设置页
    openSetting({ type, control }) {
      switch (type) {
        case "field": {
          if (this.control) {
            if (control._cid == this.control?._cid && control.code == this.control?.code) return
          }
          this.control = control
          break
        }
        case "system-field": {
          this.settingType = ""
          this.control = control
          return
        }
        case "overviewInfo":
        case "btnArray":
        case "progress": {
          if (this.settingType == type) return
          this.control = null
          break
        }
        case "reset": {
          this.settingType = ""
          this.control = null
          return
        }
      }
      this.settingType = type
      this.$refs.settingContent.init()
    },
    // 校验所有字段
    checkFields() {
      let refIds = this.allFields
        .filter(field => field._type === "ref" && !field.oneFlag)
        .map(field => field._cid)
      for (const field of this.allFields) {
        switch (field._type) {
          case "number":
          case "money":
            if (this._.isEqual(field.valueRange, [undefined, undefined])) {
              field.valueRange = []
            }
            break
          case "text":
            if (this._.isEqual(field.lenLimit, [undefined, undefined])) {
              field.lenLimit = []
            }
            break
          case "option": {
            if (this._.isEqual(field.countLimit, [undefined, undefined])) {
              field.countLimit = []
            }
            let values = field.options.map(o => o.value)
            if (this._.uniq(values).length != values.length) {
              return {
                result: false,
                msg: `选择控件[${field.name}]中不能包含重复的值`
              }
            }
            if (this._.isArray(field.defaultVal)) {
              field.defaultVal = field.defaultVal?.filter(val => values.includes(val))
            }
            break
          }
          case "ref":
            if (field.oneFlag) {
              if (!this._.isEmpty(field.defaultVal) && refIds.includes(field.defaultVal.id)) {
                field.defaultVal = {}
              }
            }
            break
        }
      }
      let mainControlCount = this.allFields.filter(field => field.main).length
      if (mainControlCount !== 1) {
        return {
          result: false,
          msg: `工作表中必须有且只有一个标题字段，当前有${mainControlCount}个`
        }
      }
      if (circleDetect(this.controlValRelyMap)) {
        return { result: false, msg: "字段默认值存在依赖循环,请检查" }
      }
      return { result: true, msg: "" }
    },
    checkFieldAttrs() {
      for (let control of this.allFields) {
        if (
          control._type === "option" &&
          control.optionType === "动态数据源" &&
          (!control.refSources?.sheetId || !control.refSources?.cid)
        ) {
          return {
            result: false,
            msg: `控件[${control.name}]是动态数据源,依赖的工作表和字段未填写`
          }
        }
        if (control._type === "option" && control.optionType === "自定义") {
          for (let option of control.options) {
            if (!option.value) {
              return {
                result: false,
                msg: `[${control.name}]设置错误，选项内容未填写！`
              }
            }
          }
        }

        // 必填校验
        let specialAttrsInfo = {}
        let commonAttrsInfo = this.controlRequiredAttrsMap[control._type]?.common
        if (control._type === "calc")
          specialAttrsInfo = this.controlRequiredAttrsMap[control._type]?.special[control.calcType]
        let allAttrs = Object.assign({ name: "名称" }, commonAttrsInfo, specialAttrsInfo)
        for (let attr of this._.keys(allAttrs)) {
          if (attr === "optionValueMap") console.log("optionValueMap", control)
          if (
            attr !== "optionValueMap"
              ? isEmpty(control[attr])
              : Object.values(control[attr]).some(e => !e)
          ) {
            return {
              result: false,
              msg: `[${control.name}]设置错误，${allAttrs[attr]}未填写！`
            }
          }
        }
      }
      return { result: true, msg: "" }
    },
    checkSnIndexOfKey() {
      for (let control of this.allFields) {
        if (control._type == "sn" && control.key !== "") {
          if (
            control.prefix.indexOf(control.key) == -1 &&
            control.suffix.indexOf(control.key) == -1
          ) {
            return {
              result: false,
              msg: `[${control.name}]设置错误，前后缀未添加关联字段`
            }
          }
        }
      }
      return { result: true, msg: "" }
    },
    // 更新数据
    update({ dataType, data } = {}) {
      switch (dataType) {
        case "fieldsGroup": {
          this.fieldsGroup = data
          break
        }
        case "systemFields": {
          this.hiddenCids = data
          break
        }
        case "overviewFields": {
          this.overviewFields = data
          break
        }
        case "progress": {
          this.progress = data
          break
        }
        case "btnArray": {
          this.btnArray = data
          break
        }
      }
    },
    // 输出
    output() {
      let res
      res = this.checkFields()
      if (!res.result) {
        !res.ignoreMsg && this.$message.error(res.msg)
        return { valid: false }
      }
      res = this.checkFieldAttrs()
      if (!res.result) {
        this.$message.error(res.msg)
        return { valid: false }
      }
      res = this.checkSnIndexOfKey()
      if (!res.result) {
        this.$message.error(res.msg)
        return { valid: false }
      }
      return {
        valid: true,
        allFields: this.allFields,
        layout: this.getLayoutInfo()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$side-menu-width: 300px;
$setting-content-wdith: 430px;

.edit-sheet-field {
  position: relative;
  height: 100%;
  display: flex;
  .side-menu {
    width: $side-menu-width;
    height: 100%;
  }
  .main-content {
    flex: auto;
    width: 0;
    display: flex;
    height: 100%;
    .fields-box {
      flex: auto;
      width: 0;
    }
    .setting-box {
      width: $setting-content-wdith;
      min-width: $setting-content-wdith;
    }
  }
}
</style>
